import React from "react";
import { Clock, X, ChevronLeft, ChevronRight, Sun } from "lucide-react";
import { todayStr } from "../../utils/dateHelpers";
import { fmtQuickTime, addDaysStr, monthCells } from "../../utils/quickAdd";

// ─── Shared date/time picking pieces (Jade, 2026-10-10) ───────────────────────
// The month calendar, quick picks and time rows, used by BOTH the quick-add
// bar's date panel (QuickAddBar.jsx) and the full add/edit form's inline
// Start / Due sections (ItemPanel in CalendarExtras.jsx), so the two always
// look and behave the same. Don't give either a private copy.

export const GRAD = "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))";

const arrowBtn = {
  display: "flex", alignItems: "center", flexShrink: 0, padding: 6, borderRadius: 17,
  border: "none", background: "transparent", color: "var(--c-text-soft)", cursor: "pointer",
};

// A date/time <input> laid invisibly over its row, so a tap opens the phone's
// own picker. Desktop Chrome only opens it from showPicker().
export function PickerOverlay({ type, value, onChange, label }) {
  return (
    <input
      type={type} value={value} aria-label={label}
      onChange={e => onChange(e.target.value)}
      onClick={e => { try { if (typeof e.currentTarget.showPicker === "function") e.currentTarget.showPicker(); } catch (_) {} }}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, border: "none", padding: 0, margin: 0, cursor: "pointer" }}
    />
  );
}

const monthOf = (d) => { const [y, m] = (d || todayStr()).split("-").map(Number); return { y, m: m - 1 }; };

// Month calendar + quick picks. Today is a filled circle; the date being edited
// (start, or due when `editingDue`) gets a ring — around today's fill when the
// two are the same day. No shaded range between start and due (Jade,
// 2026-10-10). On the due side only days before TODAY are disabled; a due date
// before the start is allowed and the caller moves the start back to it (Jade,
// 2026-10-11 — a Week view slot tap starts the item on that day, which used to
// lock out today).
export function MonthPicker({ start, due = "", isTask = false, editingDue = false, weekStart = "sunday", onPick }) {
  const today = todayStr();
  const focus = (editingDue ? (due || start) : start) || today;
  const [view, setView] = React.useState(() => monthOf(focus));
  // Follow the date being edited (a pick, or switching Start/Due) to its month.
  React.useEffect(() => { setView(monthOf(focus)); }, [focus]);

  const mondayFirst = weekStart === "monday";
  // Only the weeks that hold a day of this month (other months' days are blank),
  // so a 5-week month doesn't leave an empty row.
  const cells = monthCells(view.y, view.m, mondayFirst)
    .filter((c, i, all) => all.slice(i - (i % 7), i - (i % 7) + 7).some(x => x.inMonth));
  const dows = mondayFirst ? ["M", "T", "W", "T", "F", "S", "S"] : ["S", "M", "T", "W", "T", "F", "S"];
  const monthName = new Date(view.y, view.m, 1).toLocaleDateString(undefined, { month: "long" });
  const shiftMonth = (n) => setView(v => { const d = new Date(v.y, v.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const blocked = (d) => isTask && editingDue && d < today; // no due date in the past
  const active = editingDue ? due : start;
  const quick = [
    ["Today", today],
    ["Tomorrow", addDaysStr(today, 1)],
    ["In 3 days", addDaysStr(today, 3)],
    ["Next week", addDaysStr(today, 7)],
  ];

  return (
    <div>
      {/* Month: name on the left, arrows on the right */}
      {/* Left edge lines up with the section's date line above it, and the
          arrows' right edge with its chevron (both 14px / 12px in from the card,
          given the 6px body padding around this). */}
      <div style={{ display: "flex", alignItems: "center", padding: "0 0 0 8px", marginBottom: 4 }}>
        {/* Month in bold, the year in a lighter weight beside it (Jade, 2026-10-11). */}
        <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: "var(--c-text)" }}>
          {monthName} <span style={{ fontWeight: 600 }}>{view.y}</span>
        </span>
        <button type="button" aria-label="Previous month" onClick={() => shiftMonth(-1)} style={arrowBtn}><ChevronLeft size={19} /></button>
        <button type="button" aria-label="Next month" onClick={() => shiftMonth(1)} style={arrowBtn}><ChevronRight size={19} /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {dows.map((d, i) => <div key={i} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: "var(--c-text-soft)", opacity: 0.8, padding: "4px 0 4px" }}>{d}</div>)}
        {cells.map(({ date: d, inMonth }) => {
          const picked = d === active;
          const isToday = d === today;
          const disabled = blocked(d);
          return (
            <button type="button" key={d} onClick={() => onPick(d)} disabled={disabled} aria-label={d} aria-pressed={picked}
              style={{ height: 38, border: "none", padding: 0, cursor: disabled ? "default" : "pointer", fontFamily: "inherit", background: "transparent",
                display: "flex", alignItems: "center", justifyContent: "center", visibility: inMonth ? "visible" : "hidden" }}>
              <span style={{
                width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                boxSizing: "border-box",
                background: isToday ? GRAD : "transparent",
                // Picked: a ring. On today's fill it sits just outside, with a gap.
                border: picked && !isToday ? "2px solid var(--c-primary)" : "2px solid transparent",
                boxShadow: picked && isToday ? "0 0 0 2px var(--c-bg-card), 0 0 0 4px var(--c-primary)" : "none",
                // Cleaner numbers (Jade, 2026-10-11): even-width digits centred on one line,
                // a lighter weight, and days already past shown softer.
                fontSize: 14, lineHeight: 1, fontVariantNumeric: "tabular-nums", fontWeight: picked || isToday ? 800 : 500,
                color: isToday ? "var(--c-on-primary)" : disabled ? "var(--c-border-mid)" : picked ? "var(--c-text-accent)" : d < today ? "var(--c-text-soft)" : "var(--c-text)",
              }}>{Number(d.slice(8, 10))}</span>
            </button>
          );
        })}
      </div>

      {/* Quick picks — set whichever date is being edited. Small pills as wide
          as their text, side by side (Jade, 2026-10-10). One row from ~380px
          wide up (checked at 393/412); a narrower phone wraps the last one
          rather than cutting it off. */}
      <div className="qp-row" style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
        {quick.map(([label, d]) => {
          const on = active === d;
          return (
            <button type="button" key={label} onClick={() => onPick(d)}
              style={{ border: "none", borderRadius: 999, padding: "5px 8px", flexShrink: 0, fontFamily: "inherit", fontSize: 11.5, lineHeight: 1.3, whiteSpace: "nowrap", fontWeight: 700, cursor: "pointer",
                background: on ? GRAD : "var(--c-bg-active)", color: on ? "var(--c-on-primary)" : "var(--c-text-soft)" }}>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const rowValue = (active) => ({
  position: "relative", fontSize: 14, fontWeight: 700, padding: "6px 12px", borderRadius: 10, cursor: "pointer",
  color: active ? "var(--c-text-accent)" : "var(--c-text-soft)", background: "var(--c-bg-active)",
});

function ClearBtn({ label, onClick }) {
  return (
    <button type="button" aria-label={label} onClick={onClick}
      style={{ border: "none", background: "transparent", color: "var(--c-text-soft)", padding: 6, marginLeft: 2, cursor: "pointer", display: "flex", position: "relative", top: 4 }}>
      <X size={15} />
    </button>
  );
}

// "All day" switch above the start time (Jade, 2026-10-10, from Google
// Calendar). On = the item has no time: it sits in the Week view's all-day row
// under the date instead of on the hour grid. On by default for events, off for
// tasks; the forms hide the time rows while it's on.
export function AllDayRow({ on, onChange }) {
  return (
    <div role="switch" aria-checked={on} onClick={() => onChange(!on)}
      style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44, cursor: "pointer" }}>
      <Sun size={18} color="var(--c-text-soft)" />
      <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>All day</span>
      <span style={{ width: 38, height: 22, borderRadius: 999, background: on ? GRAD : "var(--c-border)", position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
        <span style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 18, height: 18, borderRadius: "50%", background: "var(--c-bg-card)", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
      </span>
    </div>
  );
}

// "🕒 Start time   [9:00 AM] ×" — tap the value to open the phone's time picker.
export function TimeRow({ label, value, onChange, emptyText = "None" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
      <Clock size={18} color="var(--c-text-soft)" />
      <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>{label}</span>
      <label style={rowValue(!!value)}>
        {value ? fmtQuickTime(value) : emptyText}
        <PickerOverlay type="time" value={value} label={label} onChange={v => onChange(v || "")} />
      </label>
      {value && <ClearBtn label={`Remove ${label.toLowerCase()}`} onClick={() => onChange("")} />}
    </div>
  );
}

// The optional end time under a start time (Jade, 2026-10-10): "+ End time"
// sits right under the "Start time" label; once picked it becomes an "End time"
// row lined up with the start time's. Only a time after the start is kept; the
// picker's own Clear sends "".
export function EndTimeRow({ start, value, onChange }) {
  const pick = v => onChange(v && v > start ? v : "");
  if (!value) {
    return (
      <div style={{ paddingLeft: 28, marginTop: -6, marginBottom: 4 }}>
        <label style={{ position: "relative", display: "inline-block", fontSize: 13.5, fontWeight: 700, color: "var(--c-text-accent)", padding: "4px 0", cursor: "pointer" }}>
          + End time
          <PickerOverlay type="time" value="" label="End time" onChange={pick} />
        </label>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44 }}>
      <span style={{ width: 18, flexShrink: 0 }} />
      <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", flex: 1 }}>End time</span>
      <label style={rowValue(true)}>
        {fmtQuickTime(value)}
        <PickerOverlay type="time" value={value} label="End time" onChange={pick} />
      </label>
      <ClearBtn label="Remove end time" onClick={() => onChange("")} />
    </div>
  );
}
